// Changes to the law, on the Legal page: when the platform applies a refreshed official text of one
// of the organisation's laws, the worker writes a notice (backend/src/services/lawRefreshNotices.ts,
// rules in backend/src/lib/lawRefreshNotices.ts) naming what changed, which of the organisation's
// generated documents were written from the older text and which of its obligations cite what
// changed. Nothing is re-scored: an obligation keeps its status until someone reviews it or the next
// compliance check re-assesses it against the new provision. Marking a notice reviewed needs
// legal:write (the server decides; `canReview` only hides the button).

import { useEffect, useRef, useState } from "react";
import { api, apiErrorMessage } from "../api/client";
import { DimensionHeading, SheetCard } from "./Drawing";
import { PortalLink } from "./PortalLink";
import { PolicyAmendmentDrafts } from "./PolicyAmendmentDrafts";
import { theme, btnSecondary, countTag, inputStyle, type as text } from "../theme";
import { fmtDate } from "../lib/format";

type ObligationChange = "added" | "flagged" | "updated" | "provision_changed" | "applicability_recheck";

interface Notice {
  id: string;
  label: string;
  laws: string[];
  previousVersion: string | null;
  newVersion: string;
  appliedAt: string;
  textChange: string;
  changedSections: string[];
  documents: Array<{ templateId: string; title: string; versionNumber: number; generatedAt: string }>;
  obligations: Array<{ controlId: string; name: string; law: string; change: ObligationChange; detail: string; status: string | null; justification?: string | null }>;
  reviewedAt: string | null;
  reviewedByName: string | null;
  reviewNote: string | null;
}

const CHANGE_LABEL: Record<ObligationChange, string> = {
  added: "New duty",
  flagged: "May no longer apply",
  updated: "Reworded",
  provision_changed: "Provision changed",
  applicability_recheck: "Applicability to re-check",
};

// A kind of change, not a standing: a plain chip, never StatusTag's met / partly / gap shapes.
// "May no longer apply" is drawn in the danger colour because it asks the most of the reader.
const changeChip = (change: ObligationChange): React.CSSProperties => ({
  ...countTag,
  display: "inline-block",
  padding: "0 6px",
  fontWeight: 600,
  ...(change === "flagged" || change === "applicability_recheck" ? { borderColor: "var(--c-danger)", color: "var(--c-dangerText)" } : {}),
});

const STATUS_LABEL: Record<string, string> = {
  COMPLIANT: "Compliant",
  IN_PROGRESS: "In progress",
  GAP: "Gap",
  NOT_STARTED: "Not started",
};

export function LawRefreshNotices() {
  const [notices, setNotices] = useState<Notice[] | null>(null);
  const [canReview, setCanReview] = useState(false);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showReviewed, setShowReviewed] = useState(false);
  const focusId = useRef(new URLSearchParams(window.location.search).get("lawChange"));

  async function load() {
    try {
      const d = await api.get<{ notices: Notice[]; canReview: boolean }>("/legal/refresh-notices");
      setNotices(d.notices);
      setCanReview(d.canReview);
    } catch {
      setNotices([]);
    }
  }
  useEffect(() => { void load(); }, []);
  useEffect(() => {
    if (!notices || !focusId.current) return;
    document.getElementById(`law-change-${focusId.current}`)?.scrollIntoView({ block: "start" });
  }, [notices]);

  async function review(id: string) {
    setSaving(id);
    setError(null);
    try {
      await api.post(`/legal/refresh-notices/${id}/review`, { note: notes[id]?.trim() || undefined });
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, "The notice could not be marked reviewed."));
    } finally {
      setSaving(null);
    }
  }

  if (!notices || notices.length === 0) return null;
  const open = notices.filter((n) => !n.reviewedAt);
  const reviewed = notices.filter((n) => n.reviewedAt);
  const shown = showReviewed ? notices : open;

  return (
    <section style={{ marginBottom: 24 }}>
      <DimensionHeading count={open.length ? `${open.length} to review` : undefined}>Changes to the law</DimensionHeading>
      <div style={{ ...text.hint, marginBottom: 10 }}>
        The platform now holds a newer official text of these laws. Your obligations keep their status: review the ones listed, and regenerate the documents written from the older text. The next compliance check assesses each obligation against the new provision.
      </div>
      {error && <div role="alert" style={{ color: theme.color.danger, fontSize: 13, marginBottom: 8 }}>{error}</div>}
      {open.length === 0 && !showReviewed && (
        <div style={{ ...text.hint, marginBottom: 8 }}>Every change has been reviewed.</div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {shown.map((n) => (
          <div key={n.id} id={`law-change-${n.id}`}>
            <SheetCard
              label={n.label}
              reference={fmtDate(n.appliedAt)}
              featured={focusId.current === n.id}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={text.body}>{n.textChange}</div>
                {n.changedSections.length > 0 && (
                  <div style={text.hint}>Sections: {n.changedSections.join("; ")}</div>
                )}

                <div>
                  <div style={text.itemTitle}>Documents written from the older text</div>
                  {n.documents.length === 0 ? (
                    <div style={text.hint}>None of your generated documents rests on the older text.</div>
                  ) : (
                    <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 13 }}>
                      {n.documents.map((d) => (
                        <li key={d.templateId}>
                          <PortalLink to="/templates" style={{ color: theme.color.accentText, textDecoration: "none" }}>{d.title}</PortalLink>
                          <span style={{ color: theme.color.muted }}> · version {d.versionNumber}, written {fmtDate(d.generatedAt)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {n.documents.length > 0 && <div style={{ marginTop: 8 }}><PolicyAmendmentDrafts noticeId={n.id} /></div>}
                </div>

                <div>
                  <div style={text.itemTitle}>Obligations to review</div>
                  {n.obligations.length === 0 ? (
                    <div style={text.hint}>None of your obligations cites a provision that changed.</div>
                  ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 4 }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid var(--c-line)", textAlign: "left", color: theme.color.muted }}>
                          <th style={{ padding: "4px 6px", fontWeight: 600 }}>Obligation</th>
                          <th style={{ padding: "4px 6px", fontWeight: 600 }}>Change</th>
                          <th style={{ padding: "4px 6px", fontWeight: 600 }}>Your status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {n.obligations.map((o) => (
                          <tr key={o.controlId} style={{ verticalAlign: "top" }}>
                            <td style={{ padding: "6px" }}>
                              <div>{o.name}</div>
                              <div style={{ color: theme.color.muted, fontSize: 12 }}>{o.detail}</div>
                              {o.justification && <div style={{ color: theme.color.subtle, fontSize: 12, marginTop: 2 }}>Your recorded reason: {o.justification}</div>}
                            </td>
                            <td style={{ padding: "6px", whiteSpace: "nowrap" }}><span style={changeChip(o.change)}>{CHANGE_LABEL[o.change]}</span></td>
                            <td style={{ padding: "6px", whiteSpace: "nowrap" }}>{o.status ? STATUS_LABEL[o.status] ?? o.status : "Not started"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {n.reviewedAt ? (
                  <div style={text.hint}>
                    Reviewed {fmtDate(n.reviewedAt)}{n.reviewedByName ? ` by ${n.reviewedByName}` : ""}{n.reviewNote ? `: ${n.reviewNote}` : ""}
                  </div>
                ) : canReview ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <input
                      aria-label={`Review note for ${n.label}`}
                      placeholder="Note (optional): what you checked or changed"
                      value={notes[n.id] ?? ""}
                      maxLength={2000}
                      onChange={(e) => setNotes((m) => ({ ...m, [n.id]: e.target.value }))}
                      style={{ ...inputStyle, flex: 1, minWidth: 220 }}
                    />
                    <button type="button" style={btnSecondary} disabled={saving === n.id} onClick={() => { void review(n.id); }}>
                      {saving === n.id ? "Saving…" : "Mark reviewed"}
                    </button>
                  </div>
                ) : (
                  <div style={text.hint}>A member who may decide legal obligations marks this reviewed.</div>
                )}
              </div>
            </SheetCard>
          </div>
        ))}
      </div>
      {reviewed.length > 0 && (
        <button type="button" style={{ ...btnSecondary, marginTop: 10 }} onClick={() => setShowReviewed((v) => !v)}>
          {showReviewed ? "Hide reviewed changes" : `Show reviewed changes (${reviewed.length})`}
        </button>
      )}
    </section>
  );
}
